import { EmptyBox } from 'components'
import { Table, Card, Checkbox } from 'semantic-ui-react'
import React, { useState } from 'react'
import styled from 'styled-components'
import { PaginationComponent } from 'components/Pagination'
import { DesktopMode, MobileMode } from 'layout'
import { format, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { InspectionService } from 'utility'
import {
    getClaimNonRmaNumber,
    getClaimRmaDisplayValues,
    getClaimRowId,
    getClaimSfoOrderNumber,
} from 'page-components/customer-complaint-log/customer-claim-inspection-utils'

const TableContainer = styled.div`
    .claim-row {
        cursor: pointer;
        transition: background 0.15s ease;

        &:hover {
            background: #f8fafc !important;
        }
    }
`

const BulkSelectionBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${p => (p.$between ? 'space-between' : 'flex-end')};
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;

    .selection-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }

    .clear-btn {
        background: transparent;
        color: #2563eb;
        border: none;
        padding: 0;
        font-size: 13px;
        cursor: pointer;
        text-decoration: underline;

        &:hover {
            color: #1d4ed8;
        }
    }

    .delete-btn {
        background: #2563eb;
        color: #fff;
        border: none;
        border-radius: 6px;
        padding: 8px 14px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;

        &:hover {
            background: #1d4ed8;
        }
    }
`

const StatusBadge = styled.span`
    display: inline-block;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.3;
    background: ${({ $variant }) => {
        if ($variant === 'closed') return '#10B981'
        if ($variant === 'quality') return '#EAB308'
        if ($variant === 'portal') return '#0D9488'
        if ($variant === 'disposition') return '#7C3AED'
        return '#64748B'
    }};
    color: #fff;
`

const PendingClaim = styled.span`
    color: #94a3b8;
    font-style: italic;
`

const formatClaimDateTime = value => {
    if (!value) return null
    try {
        const date =
            typeof value === 'string' ? parseISO(value) : new Date(value)
        if (Number.isNaN(date.getTime())) {
            return String(value)
        }
        return format(date, 'yyyy-MM-dd HH:mm')
    } catch {
        return null
    }
}

const formatRelativeActivity = value => {
    if (!value) return '—'
    const formatted = formatClaimDateTime(value)
    if (!formatted) return '—'
    const date =
        typeof value === 'string' ? parseISO(value) : new Date(value)
    const diffMs = Date.now() - date.getTime()
    if (diffMs >= 0 && diffMs < 120000) {
        return 'just now'
    }
    return formatted
}

const getStatusBadgeVariant = item => {
    if (item?.IsClosed) return 'closed'
    const label = String(item?.StatusLabel || '').toLowerCase()
    if (label.includes('quality')) return 'quality'
    if (label.includes('disposition')) return 'disposition'
    if (label.includes('customer service claim')) return 'portal'
    return 'default'
}

const ClaimStatusCell = ({ item }) => {
    const label = item?.IsClosed
        ? 'Closed'
        : item?.StatusLabel || 'In progress'
    return <StatusBadge $variant={getStatusBadgeVariant(item)}>{label}</StatusBadge>
}

const ClaimRmaCell = ({ item }) => {
    const values = getClaimRmaDisplayValues(item)
    if (!values.length) {
        return <PendingClaim>—</PendingClaim>
    }
    if (values.length === 1) {
        return <span>{values[0]}</span>
    }
    return (
        <span>
            {values.map(value => (
                <div key={value}>{value}</div>
            ))}
        </span>
    )
}

const ClaimNonRmaCell = ({ item }) => {
    const value = getClaimNonRmaNumber(item)
    if (!value) {
        return <PendingClaim>—</PendingClaim>
    }
    return <span>{value}</span>
}

const ClaimSfoOrderCell = ({ item }) => {
    const value = getClaimSfoOrderNumber(item)
    if (!value) {
        return <PendingClaim>—</PendingClaim>
    }
    return <span>{value}</span>
}

const useClaimRowNavigation = () => {
    const navigate = useNavigate()
    const [openingId, setOpeningId] = useState(null)

    const openClaim = async item => {
        const auditId = item?.AuditId
        if (!auditId || openingId) return
        setOpeningId(auditId)
        try {
            if (item?.AuditTemplateId) {
                navigate(
                    `/app/customer-defect/inspection/${item.AuditTemplateId}`,
                )
                return
            }
            const audit = await InspectionService.getAudit(String(auditId))
            const templateId =
                audit?.AuditTemplateId || audit?.AuditTemplateID
            if (templateId) {
                navigate(`/app/customer-defect/inspection/${templateId}`)
            }
        } catch {
            // ignore navigation errors
        } finally {
            setOpeningId(null)
        }
    }

    return { openClaim, openingId }
}

const stopRowClick = e => {
    e.stopPropagation()
}

const ClaimListRow = ({
    item,
    onOpen,
    opening,
    selected,
    onToggleSelect,
}) => (
    <Table.Row
        className="claim-row"
        onClick={() => onOpen(item)}
        style={{ opacity: opening ? 0.6 : 1 }}
    >
        <Table.Cell collapsing onClick={stopRowClick}>
            <div className="d-flex justify-content-center">
                <Checkbox
                    checked={selected}
                    onChange={(e, { checked }) => onToggleSelect(checked)}
                />
            </div>
        </Table.Cell>
        <Table.Cell>
            <ClaimRmaCell item={item} />
        </Table.Cell>
        <Table.Cell>
            <ClaimNonRmaCell item={item} />
        </Table.Cell>
        <Table.Cell>
            <ClaimSfoOrderCell item={item} />
        </Table.Cell>
        <Table.Cell>{item.CustomerName || '—'}</Table.Cell>
        <Table.Cell>
            <ClaimStatusCell item={item} />
        </Table.Cell>
        <Table.Cell>{item.OriginatorName || '—'}</Table.Cell>
        <Table.Cell>
            {formatClaimDateTime(item.Phase1CompletedOn) || '(not yet)'}
        </Table.Cell>
        <Table.Cell>
            {formatRelativeActivity(item.LastActivityOn)}
        </Table.Cell>
    </Table.Row>
)

const ClaimListCard = ({
    item,
    onOpen,
    opening,
    selected,
    onToggleSelect,
}) => (
    <Card
        fluid
        className="mb-3"
        style={{ cursor: 'pointer', opacity: opening ? 0.6 : 1 }}
        onClick={() => onOpen(item)}
    >
        <Card.Content>
            <Card.Header>
                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <div onClick={stopRowClick}>
                        <Checkbox
                            checked={selected}
                            onChange={(e, { checked }) => onToggleSelect(checked)}
                        />
                    </div>
                </div>
            </Card.Header>
            <Card.Meta>
                <div>
                    <b>Customer:</b> {item.CustomerName || '—'}
                </div>
                <div className="mt-1">
                    <b>RMA#:</b> <ClaimRmaCell item={item} />
                </div>
                <div className="mt-1">
                    <b>Non RMA#:</b> <ClaimNonRmaCell item={item} />
                </div>
                <div className="mt-1">
                    <b>SFO/Order #:</b> <ClaimSfoOrderCell item={item} />
                </div>
                <div className="mt-1">
                    <b>Status:</b> <ClaimStatusCell item={item} />
                </div>
                <div className="mt-1">
                    <b>Originator:</b> {item.OriginatorName || '—'}
                </div>
                <div className="mt-1">
                    <b>Filed (Phase 1):</b>{' '}
                    {formatClaimDateTime(item.Phase1CompletedOn) || '(not yet)'}
                </div>
                <div className="mt-1">
                    <b>Last Activity:</b>{' '}
                    {formatRelativeActivity(item.LastActivityOn)}
                </div>
            </Card.Meta>
        </Card.Content>
    </Card>
)

export const CustomerDefectReportList = ({
    data = [],
    loading,
    filter,
    pageChanged,
    totalCount = 0,
    selectedIds = [],
    onSelectionChange,
    onBulkDelete,
}) => {
    const pageSize = filter?.pageSize || filter?.PageSize || 50
    const activePage = filter?.pageNumber || filter?.PageNumber || 1
    const { openClaim, openingId } = useClaimRowNavigation()
    const pageIds = (data || []).map(getClaimRowId).filter(Boolean)
    const allPageSelected =
        pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id))
    const somePageSelected =
        pageIds.some(id => selectedIds.includes(id)) && !allPageSelected

    const togglePageSelection = checked => {
        if (!onSelectionChange) return
        if (checked) {
            onSelectionChange(Array.from(new Set([...selectedIds, ...pageIds])))
        } else {
            onSelectionChange(selectedIds.filter(id => !pageIds.includes(id)))
        }
    }

    const toggleRowSelection = (item, checked) => {
        if (!onSelectionChange) return
        const id = getClaimRowId(item)
        if (!id) return
        if (checked) {
            onSelectionChange([...selectedIds, id])
        } else {
            onSelectionChange(selectedIds.filter(selectedId => selectedId !== id))
        }
    }

    const clearAllSelection = () => {
        if (onSelectionChange) onSelectionChange([])
    }

    const renderSelectionToolbar = ({ showSelectAll = false } = {}) => {
        if (!data.length || !onSelectionChange) return null

        const selectAllCheckbox =
            showSelectAll && pageIds.length > 0 ? (
                <Checkbox
                    label={
                        allPageSelected
                            ? 'Unselect all on page'
                            : 'Select all on page'
                    }
                    checked={allPageSelected}
                    indeterminate={somePageSelected}
                    onChange={(e, { checked }) => togglePageSelection(checked)}
                />
            ) : null

        if (selectedIds.length > 0) {
            return (
                <BulkSelectionBar $between>
                    {selectAllCheckbox || <span />}
                    <div className="selection-actions">
                        <span>
                            {selectedIds.length} claim
                            {selectedIds.length === 1 ? '' : 's'} selected
                        </span>
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={clearAllSelection}
                        >
                            Clear all
                        </button>
                        {onBulkDelete && (
                            <button
                                type="button"
                                className="delete-btn"
                                onClick={onBulkDelete}
                            >
                                Delete Selected
                            </button>
                        )}
                    </div>
                </BulkSelectionBar>
            )
        }

        if (selectAllCheckbox) {
            return <BulkSelectionBar>{selectAllCheckbox}</BulkSelectionBar>
        }

        return null
    }

    return (
        <TableContainer>
            <DesktopMode>
                {renderSelectionToolbar()}
                <Table celled selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell collapsing>
                                <div className="d-flex justify-content-center">
                                    {pageIds.length > 0 && (
                                        <Checkbox
                                            checked={allPageSelected}
                                            indeterminate={somePageSelected}
                                            onChange={(e, { checked }) =>
                                                togglePageSelection(checked)
                                            }
                                        />
                                    )}
                                </div>
                            </Table.HeaderCell>
                            <Table.HeaderCell>RMA#</Table.HeaderCell>
                            <Table.HeaderCell>Non RMA#</Table.HeaderCell>
                            <Table.HeaderCell>SFO/Order #</Table.HeaderCell>
                            <Table.HeaderCell>Customer</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                            <Table.HeaderCell>Originator</Table.HeaderCell>
                            <Table.HeaderCell>Filed (Phase 1)</Table.HeaderCell>
                            <Table.HeaderCell>Last Activity</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {data?.map(item => {
                            const rowId = getClaimRowId(item)
                            return (
                                <ClaimListRow
                                    key={rowId || item.ClaimNumber}
                                    item={item}
                                    onOpen={openClaim}
                                    opening={openingId === item.AuditId}
                                    selected={selectedIds.includes(rowId)}
                                    onToggleSelect={checked =>
                                        toggleRowSelection(item, checked)
                                    }
                                />
                            )
                        })}
                    </Table.Body>
                </Table>
                {!!data?.length && !loading && (
                    <div className="w-100 justify-content-end d-flex mt-3">
                        <PaginationComponent
                            activePage={activePage}
                            pageChanged={pageChanged}
                            pageSize={pageSize}
                            totalCount={totalCount}
                        />
                    </div>
                )}
                {(!data || !data.length) && !loading && <EmptyBox />}
            </DesktopMode>
            <MobileMode>
                {renderSelectionToolbar({ showSelectAll: true })}
                {data?.length
                    ? data.map(item => {
                          const rowId = getClaimRowId(item)
                          return (
                              <ClaimListCard
                                  key={rowId || item.ClaimNumber}
                                  item={item}
                                  onOpen={openClaim}
                                  opening={openingId === item.AuditId}
                                  selected={selectedIds.includes(rowId)}
                                  onToggleSelect={checked =>
                                      toggleRowSelection(item, checked)
                                  }
                              />
                          )
                      })
                    : !loading && <EmptyBox />}
                {!!data?.length && !loading && (
                    <div className="w-100 justify-content-end d-flex mt-3">
                        <PaginationComponent
                            activePage={activePage}
                            pageChanged={pageChanged}
                            totalCount={totalCount}
                            pageSize={pageSize}
                        />
                    </div>
                )}
            </MobileMode>
        </TableContainer>
    )
}
